import { Button } from '@mui/material';
import { Show, useIsMobile } from '@side/pantry';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { ListingSummaryCardMobile } from './ListingSummaryCardMobile';
import { ListingSummaryDesktop } from './ListingSummaryDesktop';
import type { ListingSummaryResult } from './useListingSummary';

const CLEARED_FIELDS = [
  'street1',
  'street2',
  'city',
  'state',
  'zip',
  'county',
  'mlsNumber',
  'mlsServiceId',
];

type Props = {
  listing: ListingSummaryResult | undefined;
  loading: boolean;
  listingId?: string;
};

export const ListingSummaryCardRenderer = ({
  listing,
  loading,
  listingId: listingIdProp,
}: Props) => {
  const { setValue } = useFormContext();
  const isMobile = useIsMobile();
  const watchedListingId = useWatch<{ listingId?: string }, 'listingId'>({
    name: 'listingId',
  });
  const listingId = listingIdProp ?? watchedListingId;

  const trackOfferEvent = useTrackOfferAnalytics();

  useEffectOnce(() => {
    trackOfferEvent(TrackOfferEvent.MyListingSummaryMountStep1, { listingId });
  });

  const handleChangeProperty = useCallback(() => {
    trackOfferEvent(TrackOfferEvent.ClickChangeProperty, { listingId });
    setValue('listingId', undefined, { shouldDirty: true });
    // The offer flow autofills the seller's agent from the linked listing, so
    // it has to go with the property.
    setValue('sellerAgent', undefined, { shouldValidate: true });
    for (const fieldName of CLEARED_FIELDS) {
      setValue(fieldName, '', { shouldValidate: true, shouldDirty: true });
    }
  }, [setValue, trackOfferEvent, listingId]);

  const changePropertyButton = (
    <Button
      variant="outlined"
      size="small"
      color="utility"
      onClick={handleChangeProperty}
    >
      Change Property
    </Button>
  );

  return (
    <Show
      when={isMobile}
      fallback={
        <ListingSummaryDesktop
          listingId={listingId ?? ''}
          loading={loading}
          coverPhotoUrl={listing?.coverPhotoUrl ?? null}
          fullAddress={listing?.formattedFullAddress ?? ''}
          interactionStatus={listing?.interactionStatus ?? ''}
          mlsNumber={listing?.mlsNumber ?? null}
          sellers={listing?.formattedSellers ?? ''}
          action={changePropertyButton}
        />
      }
    >
      <ListingSummaryCardMobile
        listingId={listingId ?? ''}
        loading={loading}
        coverPhotoUrl={listing?.coverPhotoUrl ?? null}
        streetAddress={listing?.formattedStreetAddress ?? ''}
        cityStateZip={listing?.formattedCityStateZip ?? ''}
        mlsNumber={listing?.mlsNumber ?? null}
        sellers={listing?.formattedSellers ?? ''}
        action={changePropertyButton}
      />
    </Show>
  );
};
